import type {
  CellDto,
  HealthDto,
  QueryDataDto,
  QueryMetaDto,
  QuerySuccessDto,
} from './dto'
import type {
  Cell,
  QueryAnalysis,
  QueryOutcome,
  QueryResultSet,
  ServiceHealth,
} from './models'
import { EMPTY_RESULT_SET } from './models'

/**
 * Defensive on purpose: the pipeline builds `Data` from live SQL output, and a
 * dropped key or a `null` row would otherwise blow up deep inside a render.
 */
function toCell(value: CellDto | undefined): Cell {
  if (value === undefined) return null
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }
  return String(value)
}

export function mapResultSet(data: QueryDataDto | undefined): QueryResultSet {
  if (!data) return EMPTY_RESULT_SET

  const columns = Array.isArray(data.Columns) ? data.Columns.map(String) : []
  const rawRows = Array.isArray(data.Rows) ? data.Rows : []

  // Normalise ragged rows to the column count so the table never has to guess.
  const rows = rawRows.map((row) => {
    const source = Array.isArray(row) ? row : []
    return columns.length > 0
      ? columns.map((_, index) => toCell(source[index]))
      : source.map(toCell)
  })

  const displayedRowCount =
    typeof data.DisplayedRowCount === 'number'
      ? data.DisplayedRowCount
      : rows.length

  const rowCount =
    typeof data.RowCount === 'number' ? data.RowCount : displayedRowCount

  const result: QueryResultSet = {
    columns,
    rows,
    rowCount,
    displayedRowCount,
    truncated: Boolean(data.Truncated) || rowCount > displayedRowCount,
  }

  if (data.Note) result.note = data.Note

  return result
}

export function mapAnalysis(meta: QueryMetaDto | undefined): QueryAnalysis {
  const analysis: QueryAnalysis = {
    hasResults: Boolean(meta?.HasResults),
    usedLlm: Boolean(meta?.UsedLLM),
    isConversational: Boolean(meta?.IsConversational),
  }

  if (meta?.AnalysisMode) analysis.analysisMode = meta.AnalysisMode
  if (typeof meta?.Investigations === 'number') {
    analysis.investigations = meta.Investigations
  }

  return analysis
}

export function mapQueryOutcome(dto: QuerySuccessDto): QueryOutcome {
  return {
    answer: typeof dto.Answer === 'string' ? dto.Answer : '',
    result: mapResultSet(dto.Data),
    conversationId: dto.ConversationId ?? null,
    analysis: mapAnalysis(dto.Meta),
    status: dto.Status ?? 'ok',
    message: dto.Message,
    suggestions: dto.Suggestions,
  }
}

export function mapHealth(dto: HealthDto): ServiceHealth {
  return {
    status: dto.status ?? 'unknown',
    application: dto.application ?? 'QAP',
    version: dto.version ?? '—',
    environment: dto.environment ?? 'unknown',
  }
}
